import React, { useEffect, useState, useRef, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UploadIcon, SparklesIcon, SendIcon, CheckCircle2Icon, CheckIcon } from 'lucide-react';
type Step = 'upload' | 'enrich' | 'export';
export function EnrichmentDemo() {
  const [activeStep, setActiveStep] = useState<Step>('upload');
  const [uploadPhase, setUploadPhase] = useState<'loading' | 'done'>('loading');
  const [enrichPhase, setEnrichPhase] = useState<'loading' | 'done'>('loading');
  const [exportPhase, setExportPhase] = useState<'loading' | 'done'>('loading');
  const uploadTimerRef = useRef<NodeJS.Timeout | null>(null);
  const enrichTimerRef = useRef<NodeJS.Timeout | null>(null);
  const exportTimerRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    startUpload();
    return () => {
      if (uploadTimerRef.current) clearTimeout(uploadTimerRef.current);
      if (enrichTimerRef.current) clearTimeout(enrichTimerRef.current);
      if (exportTimerRef.current) clearTimeout(exportTimerRef.current);
    };
  }, []);
  const startUpload = () => {
    setUploadPhase('loading');
    if (uploadTimerRef.current) clearTimeout(uploadTimerRef.current);
    uploadTimerRef.current = setTimeout(() => setUploadPhase('done'), 1200);
  };
  const startEnrich = () => {
    setEnrichPhase('loading');
    if (enrichTimerRef.current) clearTimeout(enrichTimerRef.current);
    enrichTimerRef.current = setTimeout(() => setEnrichPhase('done'), 1400);
  };
  const startExport = () => {
    setExportPhase('loading');
    if (exportTimerRef.current) clearTimeout(exportTimerRef.current);
    exportTimerRef.current = setTimeout(() => setExportPhase('done'), 1500);
  };
  const handleTabSwitch = (step: Step) => {
    setActiveStep(step);
    if (step === 'upload') startUpload();
    if (step === 'enrich') startEnrich();
    if (step === 'export') startExport();
  };
  return <section className="py-24 bg-white" data-id="element-2596">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2597">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2598">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-id="element-2599">
            See local business data{' '}
            <span className="text-blue-600" data-id="element-2600">enrichment</span> in action
          </h2>
          <p className="text-xl text-slate-600 leading-relaxed" data-id="element-2601">
            Upload records, enrich with business intelligence, and launch
            personalized outreach — all from one platform.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mb-14" data-id="element-2602">
          <button onClick={() => handleTabSwitch('upload')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'upload' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-2603">
            <UploadIcon className="w-4 h-4" data-id="element-2604" />
            Upload records
          </button>
          <button onClick={() => handleTabSwitch('enrich')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'enrich' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-2605">
            <SparklesIcon className="w-4 h-4" data-id="element-2606" />
            Enrich data
          </button>
          <button onClick={() => handleTabSwitch('export')} className={`flex items-center gap-2 px-6 py-3 rounded-full text-base font-semibold transition-all duration-200 ${activeStep === 'export' ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`} data-id="element-2607">
            <SendIcon className="w-4 h-4" data-id="element-2608" />
            Launch outreach
          </button>
        </div>

        <AnimatePresence mode="wait" data-id="element-2609">
          {activeStep === 'upload' && <motion.div key="upload" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-2610">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-2611">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-2612">
                  Add records
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-2613">
                  Search in Openmart or upload a CSV.
                </p>
                <div className="space-y-5" data-id="element-2614">
                  <div data-id="element-2615">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2616">
                      File name
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-2617">
                      target_accounts.csv
                    </div>
                  </div>
                  <div data-id="element-2618">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2619">
                      Record count
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-2620">
                      1,500 rows
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-2621">
                  {uploadPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-2622">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-2623" />
                      Processing records...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-2624">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-2625" />
                      1,500 records ready
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-2626">
                {uploadPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-2627">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-2628" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-2629">
                      Processing local business records...
                    </p>
                  </div>}
                {uploadPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-2630">
                    <div className="flex items-center justify-between mb-6" data-id="element-2631">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-2632">
                        Records ready for enrichment
                      </h3>
                    </div>
                    <div className="space-y-3" data-id="element-2633">
                      {["Joe's Pizza", 'Main Street Cafe', 'Downtown Hardware'].map((name, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.15,
                  duration: 0.3
                }} className="bg-white rounded-2xl p-5 shadow-sm flex items-center justify-between" data-id="element-2634">
                          <div data-id="element-2635">
                            <h4 className="text-base font-bold text-slate-900" data-id="element-2636">
                              {name}
                            </h4>
                            <p className="text-xs text-slate-500" data-id="element-2637">
                              Ready for enrichment
                            </p>
                          </div>
                          <CheckCircle2Icon className="w-5 h-5 text-green-500" data-id="element-2638" />
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'enrich' && <motion.div key="enrich" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-2639">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-2640">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-2641">
                  Enrich data
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-2642">
                  Select business intelligence fields.
                </p>
                <div className="space-y-3" data-id="element-2643">
                  {['Revenue estimate', 'Employee count', 'Tech stack', 'Industry tags'].map((field, i) => <div key={i} className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-100" data-id="element-2644">
                      <div className="w-5 h-5 rounded bg-blue-600 flex items-center justify-center" data-id="element-2645">
                        <CheckIcon className="w-3 h-3 text-white" data-id="element-2646" />
                      </div>
                      <span className="text-sm font-medium text-slate-700" data-id="element-2647">
                        {field}
                      </span>
                    </div>)}
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-2648">
                  {enrichPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-2649">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-2650" />
                      Enriching profiles...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-2651">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-2652" />
                      Enrichment complete
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-2653">
                {enrichPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-2654">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-2655" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-2656">
                      Enriching business intelligence...
                    </p>
                  </div>}
                {enrichPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} data-id="element-2657">
                    <div className="flex items-center justify-between mb-6" data-id="element-2658">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-2659">
                        Enriched business data
                      </h3>
                    </div>
                    <div className="space-y-3" data-id="element-2660">
                      {[{
                  name: "Joe's Pizza",
                  revenue: '$1.2M/yr',
                  employees: '12 employees',
                  techStack: 'Square, Toast POS',
                  industry: 'Restaurant'
                }, {
                  name: 'Main Street Cafe',
                  revenue: '$680K/yr',
                  employees: '8 employees',
                  techStack: 'Shopify, Clover',
                  industry: 'Cafe & Bakery'
                }, {
                  name: 'Downtown Hardware',
                  revenue: '$2.4M/yr',
                  employees: '22 employees',
                  techStack: 'QuickBooks, Lightspeed',
                  industry: 'Retail'
                }].map((biz, idx) => <motion.div key={idx} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: idx * 0.15,
                  duration: 0.3
                }} className="bg-white rounded-2xl p-5 shadow-sm border border-blue-100" data-id="element-2661">
                          <div className="flex items-center justify-between mb-2" data-id="element-2662">
                            <h4 className="text-base font-bold text-slate-900" data-id="element-2663">
                              {biz.name}
                            </h4>
                            <span className="text-xs font-medium text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full" data-id="element-2664">
                              {biz.industry}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2" data-id="element-2665">
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-md" data-id="element-2666">
                              <CheckIcon className="w-3 h-3 text-green-600" data-id="element-2667" />{' '}
                              {biz.revenue}
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 px-2 py-1 rounded-md" data-id="element-2668">
                              <CheckIcon className="w-3 h-3 text-blue-600" data-id="element-2669" />{' '}
                              {biz.employees}
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 bg-purple-50 px-2 py-1 rounded-md" data-id="element-2670">
                              <CheckIcon className="w-3 h-3 text-purple-600" data-id="element-2671" />{' '}
                              {biz.techStack}
                            </span>
                          </div>
                        </motion.div>)}
                    </div>
                  </motion.div>}
              </div>
            </motion.div>}

          {activeStep === 'export' && <motion.div key="export" initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.25
        }} className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start" data-id="element-2672">
              <div className="lg:col-span-4 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10" data-id="element-2673">
                <h3 className="text-lg font-bold text-slate-900 mb-1" data-id="element-2674">
                  Launch outreach
                </h3>
                <p className="text-sm text-slate-500 mb-8" data-id="element-2675">
                  Send personalized email sequences.
                </p>
                <div className="space-y-5" data-id="element-2676">
                  <div data-id="element-2677">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2678">
                      Sequence
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700 border-2 border-blue-600" data-id="element-2679">
                      Initial Outreach + Follow Up
                    </div>
                  </div>
                  <div data-id="element-2680">
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-2681">
                      Recipients
                    </label>
                    <div className="w-full px-5 py-3.5 bg-white rounded-2xl text-base shadow-sm text-slate-700" data-id="element-2682">
                      1,500 enriched contacts
                    </div>
                  </div>
                </div>
                <div className="mt-8 flex items-center justify-center gap-2 py-4 text-sm font-semibold" data-id="element-2683">
                  {exportPhase === 'loading' ? <span className="flex items-center gap-2 text-blue-600" data-id="element-2684">
                      <motion.div animate={{
                  rotate: 360
                }} transition={{
                  repeat: Infinity,
                  duration: 1,
                  ease: 'linear'
                }} className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" data-id="element-2685" />
                      Preparing sequence...
                    </span> : <span className="flex items-center gap-2 text-green-600" data-id="element-2686">
                      <CheckCircle2Icon className="w-4 h-4" data-id="element-2687" />
                      Sequence ready
                    </span>}
                </div>
              </div>
              <div className="lg:col-span-8 bg-slate-50 rounded-[2.5rem] p-8 sm:p-10 min-h-[420px]" data-id="element-2688">
                {exportPhase === 'loading' && <div className="h-full min-h-[350px] flex flex-col items-center justify-center" data-id="element-2689">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                repeat: Infinity,
                duration: 1,
                ease: 'linear'
              }} className="w-10 h-10 rounded-full mb-4" style={{
                borderWidth: '3px',
                borderColor: '#e2e8f0',
                borderTopColor: '#156ef6',
                borderStyle: 'solid'
              }} data-id="element-2690" />
                    <p className="text-lg font-medium text-slate-500" data-id="element-2691">
                      Loading email sequence...
                    </p>
                  </div>}
                {exportPhase === 'done' && <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4
            }} className="flex flex-col min-h-[350px]" data-id="element-2692">
                    <div className="flex items-center justify-between mb-4" data-id="element-2693">
                      <h3 className="text-lg font-bold text-slate-900" data-id="element-2694">
                        Email sequence preview
                      </h3>
                    </div>
                    <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white" data-id="element-2695">
                      <img src="https://cdn.magicpatterns.com/uploads/ua17rCrXcbRSJeVyikT2pZ/outreach_email_template.png" alt="Openmart email sequence editor with personalized outreach template" className="w-full h-auto block" loading="lazy" data-id="element-2696" />
                    </div>
                    <a href="http://app.openmart.com/register" target="_blank" rel="noopener noreferrer" className="mt-6 w-full px-6 py-3.5 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700 transition-colors text-base text-center block max-w-xs mx-auto" data-id="element-2697">
                      Start sending outreach
                    </a>
                  </motion.div>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}